import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { PageSection } from 'adc-ui-components';
import { flagEnabled } from '../../helpers/featureFlags';
import { buildNbaQueue, dismissNba } from '../../actions/nba';
import { getSikOrder } from '../../actions/sik';
import { getDeviceOutage } from '../../actions/outage';
import Accordion from '../../components/Accordion';
import NextBestActions from '../../components/NextBestActions';
import CommonSolutions from '../../components/CommonSolutions';
import LoadingWrapper from '../../components/LoadingWrapper';
import EidServiceHero from '../../components/EidServiceHero';
import DeviceCountHero from '../../components/DeviceCountHero';
import PreactiveBanner from '../../components/PreactiveBanner';
import { getPreactiveDetails } from '../../helpers/preactive';
import { getCmsSupport } from '../../actions/cmsSupport';
import DeviceCardGroup from '../../components/DeviceCardGroup';
import CmsSupport from '../../components/CmsSupport';
import { buildByServiceFromAccount } from '../../helpers/devices';
import DataUsageComponent from '../../components/DataUsage';


const Devices = ({
  loading,
  devicesLoading,
  devicesError,
  byService,
  accordionData,
  nbas,
  nbaLoading,
  deviceCount,
  showPreactive,
  preactiveDetails,
  handleBuildNbaQueue,
  handleDismissNba,
  handleGetSikOrder,
  handleGetCmsSupport,
  cmsSupportData,
  cmsSupportLoading,
  isAccountResolved,
  accountStatus,
  isPrepaid,
  handleFetchOutage,
}) => {
  useEffect(() => {
    handleBuildNbaQueue();
    handleFetchOutage();
  }, [handleBuildNbaQueue, handleFetchOutage]);

  useEffect(() => {
    handleGetSikOrder();
  }, [handleGetSikOrder]);

  useEffect(() => {
    if (isAccountResolved && !isPrepaid) {
      handleGetCmsSupport();
    }
  }, [isAccountResolved, handleGetCmsSupport, isPrepaid]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let hasScrolledToUsage = false;
    let retryTimer = null;

    const moveHash = () => {
      const { hash } = window.location;
      if (hash !== '#usage' || hasScrolledToUsage) return;

      const usageElement = document.getElementById('usage');
      if (!usageElement) {
        retryTimer = window.setTimeout(moveHash, 50);
        return;
      }

      const fixedHeaderOffset = 85;
      const top = usageElement.getBoundingClientRect().top + window.pageYOffset - fixedHeaderOffset;

      hasScrolledToUsage = true;
      window.scrollTo(0, Math.max(top, 0));
    };

    moveHash();
    window.addEventListener('hashchange', moveHash);

    return () => {
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }

      window.removeEventListener('hashchange', moveHash);
    };
  }, []);

  const heroHeaderEnabled = flagEnabled('enableDevicesHeroHeader');

  return (
    <div className="devices-overview">
      {accordionData ? (
        <Accordion
          shortDescription={accordionData.shortDescription}
          detailedDescription={accordionData.detailedDescription}
        />
      ) : null}
      {loading ? (
        <LoadingWrapper />
      ) : (
        <>
          {deviceCount === 0 && showPreactive && heroHeaderEnabled && (
            <PreactiveBanner details={preactiveDetails} />
          )}
          <PageSection className="ui-grey section--devices">
            <NextBestActions
              nbas={nbas}
              onDismiss={nba => handleDismissNba(nba)}
              loading={nbaLoading}
            />
            <div className="hero-flex-wrap hero-flex-wrap--at-768 mb18">
              <section className="hero-flex-wrap__main overview-section">
                {flagEnabled('enableElevatedIssueDetection') ? (
                  <EidServiceHero
                    heroTitle="Devices"
                    trackingName="devices"
                  />
                ) : (
                  <DeviceCountHero />
                )}
              </section>
              <aside className="hero-flex-wrap__aside">
                <CommonSolutions />
              </aside>
            </div>
            {devicesLoading && <LoadingWrapper />}
            {devicesError && !devicesLoading && (
            <div className="card card--danger mb18">
              <div className="card__content">
                <p>We were unable to retrieve device data at this time, please try again.</p>
              </div>
            </div>
            )}

            {!devicesLoading && !devicesError && byService.map(group => (
              <div key={group.title} className="mb18">
                <div className="hgroup">
                  <h2>{group.title}</h2>
                  <p>{group.description}</p>
                </div>
                <DeviceCardGroup devices={group.devices} deviceType={group.type} />
              </div>
            ))}

            <DataUsageComponent />
          </PageSection>
          <CmsSupport
            data={cmsSupportData}
            loading={cmsSupportLoading}
            isAccountResolved={isAccountResolved}
            accountStatus={accountStatus}
            isPrepaid={isPrepaid}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({
  account: {
    loading, devices: accountDevices = {}, error: accountError, data,
  } = {},
  consolidatedOutage,
  outage,
  nba,
  sik = {},
  cmsSupport: supportState = {},
}) => {
  const outageData = flagEnabled('enableOutageLob')
    ? consolidatedOutage.data
    : outage.data;
  const customerMessage = outageData && outageData.customerMessage;
  const accordionData = customerMessage && customerMessage.impactStyle === 'Future'
    ? customerMessage.messages
    : null;
  const devices = (accountDevices.list) || [];
  const accountData = data || {};

  return {
    cmsSupportData: supportState.data,
    cmsSupportLoading: supportState.loading,
    loading,
    isAccountResolved: !loading && Boolean(data),
    accordionData,
    nbas: nba.details || [],
    nbaLoading: nba.loading,
    deviceCount: devices.filter(({ lob }) => lob !== 'mobile').length,
    showPreactive: sik.status === 'READY',
    preactiveDetails: getPreactiveDetails(sik.sikOrder, data),
    devicesLoading: loading,
    devicesError: !!accountError,
    accountStatus: accountData.status,
    isPrepaid: Boolean(accountData.prepaid),
    byService: buildByServiceFromAccount(accountDevices.list),
  };
};

const mapDispatchToProps = {
  handleBuildNbaQueue: buildNbaQueue,
  handleDismissNba: dismissNba,
  handleGetSikOrder: getSikOrder,
  handleGetCmsSupport: getCmsSupport,
  handleFetchOutage: getDeviceOutage,
};

export default connect(mapStateToProps, mapDispatchToProps)(Devices);
